// src/pages/Documents.js
// Admin page for managing documents (waivers, terms, opt-ins, etc.) that the
// app references in signup flows, membership purchases, etc.
//
// Stage 1 of the Documents feature: this file owns the CRUD UI and Firestore
// data model only. Signup-flow integration and the membership-purchase waiver
// check are separate stages.
//
// Firestore collection: clubDocuments/{id}
//   name              string         Display name (e.g. "Junior Waiver")
//   description       string         Admin-only note about purpose
//   content           string         Rich-text HTML body (Quill output)
//   audience          string[]       Drives both list filtering and read access:
//                                    everyone | parents | adults | coaches | admins
//                                    "everyone" → publicly readable, no auth.
//   actionType        string         view | accept | accept_or_reject | signature
//   expirationDays    number         Days until acceptance expires. 0 = never.
//   enabled           boolean
//   sortOrder         number
//   createdAt, updatedAt
//
// Acceptance records are stored on each user as users.documentAcceptances[docId]
// — but this page doesn't read those yet; that's wired in later stages.

import React, { useEffect, useRef, useState } from 'react';
import {
  collection, getDocs, getDoc, doc, addDoc, updateDoc, deleteDoc, serverTimestamp,
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db, auth } from '../firebase';
import Layout from '../components/Layout';
import EmailBodyEditor from '../components/EmailBodyEditor';
import GroupRentalInvitePanel from '../components/GroupRentalInvitePanel';
import { useBranding } from '../contexts/BrandingContext';
import {
  Box, Typography, Paper, Grid, TextField, MenuItem, Button, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions, Chip, Divider,
  FormControlLabel, Switch, Alert, CircularProgress, Tooltip,
  Checkbox,
  Tabs, Tab,
  Select,
  InputLabel,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import DescriptionIcon from '@mui/icons-material/Description';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { logError } from '../utils/errorLogger';

// Audience tags — each doc carries one or more of these. They drive both
// what the user sees in listings AND what the Firestore rule allows them
// to read. "Everyone" is special: docs tagged Everyone are publicly
// readable (no auth needed) regardless of other tags on the doc.
//
// Keep these `value` strings stable — they're the literal field values
// stored in clubDocuments.audience[] AND referenced in the Firestore rule.
const AUDIENCES = [
  { value: 'everyone', label: 'Everyone (Public)' },
  { value: 'parents',  label: 'Parents' },
  { value: 'adults',   label: 'Adults' },
  { value: 'coaches',  label: 'Coaches' },
  { value: 'admins',   label: 'Admins' },
];

const ACTION_TYPES = [
  { value: 'view',             label: 'View only' },
  { value: 'accept',           label: 'Must accept' },
  { value: 'accept_or_reject', label: 'Accept or reject' },
  { value: 'signature',        label: 'Signature required' },
];

const ACTION_TYPE_LABELS = Object.fromEntries(ACTION_TYPES.map(a => [a.value, a.label]));

const emptyDoc = {
  name: '',
  slug: '',
  description: '',
  content: '',
  audience: [],
  actionType: 'accept',
  expirationDays: '',
  enabled: true,
  sortOrder: 0,
};

// Email template form shape. Templates live in `emailTemplates/{id}` and are
// consumed by Cloud Functions via the renderEmail helper. Adding a new
// template requires CF wiring, so this admin page only edits existing ones.
const emptyTemplate = {
  name: '',
  description: '',
  subject: '',
  html: '',
  placeholders: [],
  enabled: true,
};

// Convert a string to a URL-safe slug. Lowercase, spaces & punctuation → dashes,
// collapse repeated dashes, trim edges. Returns '' if nothing usable remains.
function toSlug(s) {
  return String(s || '')
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')   // strip punctuation
    .replace(/\s+/g, '-')        // spaces → dashes
    .replace(/-+/g, '-')         // collapse repeated dashes
    .replace(/^-+|-+$/g, '');    // trim leading/trailing dashes
}

// ── Recipient list helpers (shared by the announcement and campaign pickers) ─
//
// Both lists arrive sorted by something the rows do not show — the
// announcement audience by last name, the campaign list by email — and both
// render "First Last". Sorted by an invisible key, a list reads as unsorted.
// These order by the name as DISPLAYED so the column the eye scans runs A-Z,
// with nameless rows sunk to the bottom by email rather than scattered
// through the alphabet under a blank.
function byDisplayName(a, b) {
  const nameOf = (x) => `${x.firstName || ''} ${x.lastName || ''}`.trim();
  const an = nameOf(a), bn = nameOf(b);
  if (!an !== !bn) return an ? -1 : 1;
  return (an || a.email).localeCompare(bn || b.email, undefined, { sensitivity: 'base' })
    || a.email.localeCompare(b.email);
}

// Every token has to land somewhere in the row, so "smith john" finds the same
// person as "john smith". An empty or whitespace-only box matches everything.
function matchesRecipientQuery(rec, query) {
  const q = String(query || '').trim().toLowerCase();
  if (!q) return true;
  const hay = `${rec.firstName || ''} ${rec.lastName || ''} ${rec.email}`.toLowerCase();
  return q.split(/\s+/).every(tok => hay.includes(tok));
}

export default function Documents() {
  const branding = useBranding();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [docs, setDocs] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyDoc);
  const [dialogOpen, setDialogOpen] = useState(false);
  // Tracks whether the admin has manually edited the slug. While false we keep
  // auto-syncing slug from name; once true we leave the slug alone.
  const [slugTouched, setSlugTouched] = useState(false);

  // Preview dialog (read-only)
  const [previewDoc, setPreviewDoc] = useState(null);

  // Quill editor ref — instance is mounted inside the edit dialog
  const quillContainerRef = useRef(null);
  const quillInstanceRef = useRef(null);
  // Most recently clicked image element inside the editor. Tracked so the
  // size preset buttons know which image to resize. Cleared when the user
  // clicks outside any image (e.g. on plain text).
  const selectedImageRef = useRef(null);
  const [hasSelectedImage, setHasSelectedImage] = useState(false);

  // ── Email Templates tab state ──────────────────────────────────────────────
  // tab: 0 = Documents, 1 = Email Templates
  const [tab, setTab] = useState(0);
  const [templates, setTemplates] = useState([]);
  const [templateForm, setTemplateForm] = useState(emptyTemplate);
  const [editingTemplateId, setEditingTemplateId] = useState(null);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  // ── Campaign (member-migration) state ─────────────────────────────────────
  const [campaignOpen, setCampaignOpen] = useState(false);
  const [campaignLoading, setCampaignLoading] = useState(false);
  const [campaignRecipients, setCampaignRecipients] = useState([]);
  const [campaignSelected, setCampaignSelected] = useState({});
  // Narrows what the list shows, never the send set — same rule as annSearch.
  const [campaignSearch, setCampaignSearch] = useState('');
  const [campaignSending, setCampaignSending] = useState(false);
  const [campaignResult, setCampaignResult] = useState(null);

  // ── Announcement state (member-announcement) ───────────────────────────────
  // Separate from the migration campaign above: different recipients (our own
  // users, not Stripe), and the only email category an unsubscribe silences.
  const [annOpen, setAnnOpen] = useState(false);
  const [annLoading, setAnnLoading] = useState(false);
  const [annRecipients, setAnnRecipients] = useState([]);
  // Audience buckets and their counts come from the server with the recipients,
  // so the dropdown's labels can't drift from what the buckets actually mean.
  const [annAudiences, setAnnAudiences] = useState([]);
  const [annAudience, setAnnAudience] = useState('adults');
  const [annSelected, setAnnSelected] = useState({});
  // Narrows what the recipient list shows. Deliberately NOT part of the send
  // set — see annShown below for why.
  const [annSearch, setAnnSearch] = useState('');
  const [annSending, setAnnSending] = useState(false);
  // "Batch 2 of 4" while a large announcement goes out in 200s.
  const [annProgress, setAnnProgress] = useState(null);
  const [annResult, setAnnResult] = useState(null);
  // The announcement is composed and sent in one dialog. Wording lives here in
  // form state until Send, which saves it before mailing — the send reads the
  // template back from Firestore, so an unsaved edit would otherwise mail the
  // previous announcement's copy.
  const [annForm, setAnnForm] = useState({ subject: '', fields: {} });
  const [annTestTo, setAnnTestTo] = useState('');
  const [annTesting, setAnnTesting] = useState(false);
  // Saved drafts. '' as the id means a new draft that has never been written.
  const [annDrafts, setAnnDrafts] = useState([]);
  const [annDraftId, setAnnDraftId] = useState('');
  const [annDirty, setAnnDirty] = useState(false);
  const [annSavingDraft, setAnnSavingDraft] = useState(false);
  const [annTemplate, setAnnTemplate] = useState(null);
  const [previewData, setPreviewData] = useState({ subject: '', html: '' });


  // ── Load documents ─────────────────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        const snap = await getDocs(collection(db, 'clubDocuments'));
        const list = snap.docs.map(d => ({ id: d.id, ...d.data() }))
          .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
        setDocs(list);
      } catch (e) {
        logError('Documents', e.message, e.stack);
        setError('Failed to load documents.');
      }
      setLoading(false);
    })();
  }, []);

  // ── Load email templates ───────────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        const snap = await getDocs(collection(db, 'emailTemplates'));
        const list = snap.docs.map(d => ({ id: d.id, ...d.data() }))
          .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        setTemplates(list);
      } catch (e) {
        logError('Documents', e.message, e.stack);
      }
    })();
  }, []);

  // ── Quill setup — initialise when the dialog opens, tear down on close ────
  // Quill is loaded from CDN to avoid bundling cost; loads once per session.
  useEffect(() => {
    if (!dialogOpen) {
      quillInstanceRef.current = null;
      selectedImageRef.current = null;
      setHasSelectedImage(false);
      return;
    }
    let cancelled = false;

    const ensureQuill = async () => {
      if (window.Quill) return window.Quill;
      // Load CSS
      if (!document.getElementById('quill-snow-css')) {
        const link = document.createElement('link');
        link.id = 'quill-snow-css';
        link.rel = 'stylesheet';
        link.href = 'https://cdn.quilljs.com/1.3.7/quill.snow.css';
        document.head.appendChild(link);
      }
      // Load script
      await new Promise((resolve, reject) => {
        const existing = document.getElementById('quill-js');
        if (existing) { existing.addEventListener('load', resolve); return; }
        const s = document.createElement('script');
        s.id = 'quill-js';
        s.src = 'https://cdn.quilljs.com/1.3.7/quill.min.js';
        s.onload = resolve;
        s.onerror = reject;
        document.head.appendChild(s);
      });
      return window.Quill;
    };

    (async () => {
      const Quill = await ensureQuill();
      if (cancelled) return;
      // Quill mounts only after the dialog has rendered the target element.
      // Small timeout gives MUI Dialog time to attach to the DOM.
      setTimeout(() => {
        if (cancelled || !quillContainerRef.current) return;
        // If already initialised (e.g. dialog re-render), skip
        if (quillInstanceRef.current) return;
        const q = new Quill(quillContainerRef.current, {
          theme: 'snow',
          placeholder: 'Document body - paste or type text here',
          modules: {
            toolbar: [
              [{ header: [1, 2, 3, false] }],
              ['bold', 'italic', 'underline', 'strike'],
              [{ list: 'ordered' }, { list: 'bullet' }],
              [{ indent: '-1' }, { indent: '+1' }],
              ['link', 'image'],
              [{ align: [] }],
              ['clean'],
            ],
          },
        });
        q.clipboard.dangerouslyPasteHTML(form.content || '');
        q.on('text-change', () => {
          // Snapshot HTML back into form state on every change.
          // (Cheap on small docs; if perf becomes an issue we can debounce.)
          setForm(f => ({ ...f, content: q.root.innerHTML }));
        });
        // Track image selection so the size preset buttons know which image
        // to act on. Click directly on an <img> selects it; click anywhere
        // else inside the editor clears the selection.
        const onEditorClick = (e) => {
          if (e.target && e.target.tagName === 'IMG') {
            selectedImageRef.current = e.target;
            setHasSelectedImage(true);
          } else {
            selectedImageRef.current = null;
            setHasSelectedImage(false);
          }
        };
        q.root.addEventListener('click', onEditorClick);
        // Store the cleanup hook on the instance so we can pull it off when
        // the dialog closes (see the cleanup branch above).
        q._onEditorClick = onEditorClick;
        quillInstanceRef.current = q;
      }, 50);
    })().catch(e => {
      logError('Documents', e.message, e.stack);
      setError('Couldn\'t load the editor — check your connection.');
    });

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dialogOpen]);


  // ── Handlers ───────────────────────────────────────────────────────────────
  // ── Campaign handlers (member-migration) ──────────────────────────────────
  // One-time: create welcome-email-junior / welcome-email-adult from the
  // original combined template, so the editor opens on existing copy.
  const [seedingWelcome, setSeedingWelcome] = React.useState(false);
  const seedWelcome = async () => {
    setSeedingWelcome(true); setError(''); setSuccess('');
    try {
      const fn = httpsCallable(getFunctions(), 'seedWelcomeTemplates');
      const r = await fn({ dryRun: false });
      const created = (r.data?.created || []).map(c => c.id).join(', ');
      const skipped = (r.data?.skipped || []).join(', ');
      setSuccess(created
        ? `Created ${created}. Your existing copy was carried over — edit it below.`
        : `Nothing created${skipped ? ` — already exists: ${skipped}` : ''}.`);
      const snap = await getDocs(collection(db, 'emailTemplates'));
      setTemplates(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (e) {
      setError(`Could not create the welcome templates: ${e.message}`);
    } finally { setSeedingWelcome(false); }
  };

  // Ordered by the name shown, then narrowed by the search box. campaignChosen
  // is derived from the LIST rather than counted out of the selection map,
  // which is what sendCampaign has always mailed: a lingering tick for someone
  // who has since registered and dropped off the list used to inflate the
  // button's count above the number actually sent.
  const campaignSorted = [...campaignRecipients].sort(byDisplayName);
  const campaignShown = campaignSorted.filter(x => matchesRecipientQuery(x, campaignSearch));
  const campaignChosen = campaignSorted.filter(x => campaignSelected[x.email]);

  const openCampaign = async () => {
    setCampaignOpen(true); setCampaignLoading(true); setCampaignResult(null);
    try {
      const functions = getFunctions();
      const fn = httpsCallable(functions, 'listUnmatchedMembers', { timeout: 300000 });
      const r = await fn({});
      const recs = r.data.recipients || [];
      setCampaignRecipients(recs);
      // First call seeds emailTemplates/member-migration — refresh the card list
      if (!templates.some(t => t.id === 'member-migration')) {
        try {
          const snap = await getDocs(collection(db, 'emailTemplates'));
          setTemplates(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        } catch (e) { /* card appears on next page load */ }
      }
      // Default: nothing selected — batch sends of 5-6 at a time
      setCampaignSelected({});
      setCampaignSearch('');
    } catch (e) {
      logError('Documents.openCampaign', e.message, e.stack);
      setCampaignResult({ severity: 'error', text: 'Failed to load recipients: ' + e.message });
    } finally { setCampaignLoading(false); }
  };

  const sendCampaign = async () => {
    const chosen = campaignChosen;
    if (chosen.length === 0) return;
    if (!window.confirm(`Send the migration email to ${chosen.length} member${chosen.length === 1 ? '' : 's'}?`)) return;
    setCampaignSending(true); setCampaignResult(null);
    try {
      const functions = getFunctions();
      const fn = httpsCallable(functions, 'sendMemberCampaign', { timeout: 300000 });
      const r = await fn({ templateId: 'member-migration', recipients: chosen.map(({ email, firstName, lastName }) => ({ email, firstName, lastName })) });
      const failedList = (r.data.results || []).filter(x => !x.ok);
      setCampaignResult({
        severity: r.data.failed === 0 ? 'success' : 'warning',
        text: `${r.data.sent} sent, ${r.data.failed} failed.` + (failedList.length ? ' Failed: ' + failedList.map(f => f.email).join(', ') : ''),
      });
      // Refresh sent badges
      await openCampaignRefresh();
    } catch (e) {
      logError('Documents.sendCampaign', e.message, e.stack);
      setCampaignResult({ severity: 'error', text: 'Send failed: ' + e.message });
    } finally { setCampaignSending(false); }
  };

  const openCampaignRefresh = async () => {
    try {
      const functions = getFunctions();
      const fn = httpsCallable(functions, 'listUnmatchedMembers', { timeout: 300000 });
      const r = await fn({});
      setCampaignRecipients(r.data.recipients || []);
    } catch (e) { /* keep existing list */ }
  };

  // ── Announcement handlers (member-announcement) ───────────────────────────
  const loadAnnouncementAudience = async () => {
    const fn = httpsCallable(getFunctions(), 'listAnnouncementAudience', { timeout: 300000 });
    const r = await fn({});
    setAnnAudiences(r.data.audiences || []);
    return r.data.recipients || [];
  };

  const ANN_ID = 'member-announcement';

  // Drafts hold what one announcement says. The template holds the shell the
  // drafts are cut from — the images, and the headline and footer a new draft
  // starts with, so the mailing address carries into each one without being
  // retyped. A draft is never overwritten by another; last month's wording
  // stays readable after this month's goes out.
  const loadDrafts = async () => {
    const snap = await getDocs(collection(db, 'announcementDrafts'));
    const list = snap.docs.map(d => ({ id: d.id, ...d.data() }))
      .sort((a, b) => (b.updatedAt?.toMillis?.() ?? 0) - (a.updatedAt?.toMillis?.() ?? 0));
    setAnnDrafts(list);
    return list;
  };

  const draftFromTemplate = (t) => ({
    subject: '',
    fields: { headline: t?.fields?.headline || '', body: '', footerText: t?.fields?.footerText || '' },
    images: t?.images || {},
  });

  const loadDraftIntoForm = (d, tpl) => {
    setAnnDraftId(d?.id || '');
    setAnnForm(d
      ? { subject: d.subject || '', fields: { ...(d.fields || {}) }, images: tpl?.images || {} }
      : draftFromTemplate(tpl));
    setAnnDirty(false);
  };

  const openAnnouncement = async () => {
    setAnnOpen(true); setAnnLoading(true); setAnnResult(null);
    setAnnTestTo(auth.currentUser?.email || '');
    try {
      const recs = await loadAnnouncementAudience();
      setAnnRecipients(recs);
      // Read the template straight from Firestore rather than the cards in
      // state: the call above seeds it on first use, so state can be a beat
      // behind, and composing against a stale copy is how wording gets lost.
      const tplSnap = await getDoc(doc(db, 'emailTemplates', ANN_ID));
      const tpl = tplSnap.exists() ? tplSnap.data() : null;
      setAnnTemplate(tpl);

      const list = await loadDrafts();
      // Open on the most recent UNSENT draft — the thing most likely still
      // being written. Everything sent is finished work, so landing on it would
      // invite editing a record of what already went out.
      const resume = list.find(d => !d.lastSentAt) || null;
      loadDraftIntoForm(resume, tpl);

      // First call seeds emailTemplates/member-announcement — refresh the cards
      if (!templates.some(t => t.id === 'member-announcement')) {
        try {
          const snap = await getDocs(collection(db, 'emailTemplates'));
          setTemplates(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        } catch (e) { /* card appears on next page load */ }
      }
      // Nothing pre-selected — an announcement goes out in batches the admin
      // picks, and "select all" on first open is how a draft becomes a send.
      setAnnSelected({});
    } catch (e) {
      logError('Documents.openAnnouncement', e.message, e.stack);
      setAnnResult({ severity: 'error', text: 'Failed to load: ' + e.message });
    } finally { setAnnLoading(false); }
  };

  const editAnn = (mut) => { setAnnForm(mut); setAnnDirty(true); };

  const annNewDraft = () => {
    if (annDirty && !window.confirm('Start a new draft? Unsaved changes to the current one are lost.')) return;
    loadDraftIntoForm(null, annTemplate);
    setAnnResult(null);
  };

  const annPickDraft = (id) => {
    if (id === annDraftId) return;
    if (annDirty && !window.confirm('Switch draft? Unsaved changes to the current one are lost.')) return;
    loadDraftIntoForm(annDrafts.find(d => d.id === id) || null, annTemplate);
    setAnnResult(null);
  };

  // Returns the draft id written to, so send can save-then-mail in one step.
  const annSaveDraft = async () => {
    if (!String(annForm.subject || '').trim()) {
      setAnnResult({ severity: 'error', text: 'Give the draft a subject before saving.' });
      return null;
    }
    setAnnSavingDraft(true);
    try {
      const payload = {
        subject: annForm.subject,
        fields: annForm.fields || {},
        updatedAt: serverTimestamp(),
        updatedBy: auth.currentUser?.email || '',
      };
      let id = annDraftId;
      if (id) {
        await updateDoc(doc(db, 'announcementDrafts', id), payload);
      } else {
        const ref = await addDoc(collection(db, 'announcementDrafts'), { ...payload, createdAt: serverTimestamp() });
        id = ref.id;
        setAnnDraftId(id);
      }
      await loadDrafts();
      setAnnDirty(false);
      return id;
    } catch (e) {
      logError('Documents.annSaveDraft', e.message, e.stack);
      setAnnResult({ severity: 'error', text: 'Could not save the draft: ' + e.message });
      return null;
    } finally { setAnnSavingDraft(false); }
  };

  const annDeleteDraft = async () => {
    const d = annDrafts.find(x => x.id === annDraftId);
    if (!d) return;
    const warn = d.lastSentAt
      ? `Delete "${d.subject}"? It was sent — deleting it removes the record of what that announcement said.`
      : `Delete the draft "${d.subject}"?`;
    if (!window.confirm(warn)) return;
    try {
      await deleteDoc(doc(db, 'announcementDrafts', annDraftId));
      const list = await loadDrafts();
      loadDraftIntoForm(list.find(x => !x.lastSentAt) || null, annTemplate);
      setAnnResult({ severity: 'success', text: 'Draft deleted.' });
    } catch (e) {
      logError('Documents.annDeleteDraft', e.message, e.stack);
      setAnnResult({ severity: 'error', text: 'Could not delete: ' + e.message });
    }
  };

  const annCurrentDraft = annDrafts.find(d => d.id === annDraftId) || null;
  // The editor leaves <p><br></p> behind when you clear it, so an "empty" body
  // is not an empty string. The server checks this too — this is just so the
  // button says why rather than failing at the end of a send.
  const annBodyHasText = !!String(annForm.fields?.body || '').replace(/<[^>]*>/g, '').trim();

  // Everything below is scoped to the CHOSEN audience, not the whole list.
  // Deriving the send set from the visible rows is what stops a selection made
  // under one audience from riding along invisibly under another.
  const annVisible = annRecipients
    .filter(x => (x.audiences || []).includes(annAudience))
    .sort(byDisplayName);
  // Opted-out addresses are never selectable; hard-bounced ones are left out of
  // "All" but can still be ticked by hand.
  const annSendable = annVisible.filter(x => !x.optedOut);
  const annChosen = annVisible.filter(x => annSelected[x.email] && !x.optedOut);

  // Search narrows what the list SHOWS and nothing else, which is the whole
  // difference between it and the audience dropdown. Audience is a real scope:
  // change it and the selection is dropped, because a tick made under one
  // audience must not ride along invisibly under another. A search is only a
  // way to find a row, so ticks survive it — search, tick, clear the box, and
  // that person is still going to get the email. Narrowing annChosen here
  // instead would turn the search box into a silent deselect.
  const annQuery = annSearch.trim();
  const annShown = annVisible.filter(x => matchesRecipientQuery(x, annQuery));

  // Switching audience clears the selection outright. Carrying ticks across a
  // filter change is how you send to someone you can no longer see.
  const changeAudience = (id) => { setAnnAudience(id); setAnnSelected({}); setAnnSearch(''); };

  // Preview and test both render the CURRENT form state, not the saved doc —
  // checking wording you haven't saved yet is the whole point of them.
  const annPreview = async () => {
    setPreviewLoading(true);
    try {
      const fn = httpsCallable(getFunctions(), 'previewEmailTemplate');
      const r = await fn({ templateId: ANN_ID, subject: annForm.subject, fields: annForm.fields, images: annForm.images || {} });
      setPreviewData(r.data);
      setPreviewOpen(true);
    } catch (e) {
      logError('Documents.annPreview', e.message, e.stack);
      setAnnResult({ severity: 'error', text: 'Preview failed: ' + e.message });
    } finally { setPreviewLoading(false); }
  };

  const annSendTest = async () => {
    const to = (annTestTo || '').trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(to)) {
      setAnnResult({ severity: 'error', text: 'Enter a valid address to send the test to.' });
      return;
    }
    setAnnTesting(true); setAnnResult(null);
    try {
      const fn = httpsCallable(getFunctions(), 'sendTestEmail');
      await fn({ templateId: ANN_ID, to, subject: annForm.subject, fields: annForm.fields, images: annForm.images || {} });
      setAnnResult({ severity: 'success', text: `Test sent to ${to}. Its unsubscribe link is live for that address.` });
    } catch (e) {
      logError('Documents.annSendTest', e.message, e.stack);
      setAnnResult({ severity: 'error', text: 'Test send failed: ' + e.message });
    } finally { setAnnTesting(false); }
  };

  const sendAnnouncementNow = async () => {
    if (annChosen.length === 0) return;
    const audLabel = (annAudiences.find(a => a.id === annAudience) || {}).label || annAudience;
    if (!window.confirm(
      `Send this announcement to ${annChosen.length} recipient${annChosen.length === 1 ? '' : 's'} in "${audLabel}"?`
      + `\n\nSubject: ${annForm.subject}`
    )) return;
    setAnnSending(true); setAnnResult(null);
    try {
      // Save first, then send that draft by id. sendAnnouncement renders from
      // the stored draft, so skipping this would mail the last saved wording
      // rather than what is on screen.
      const draftId = await annSaveDraft();
      if (!draftId) { setAnnSending(false); return; }

      // sendAnnouncement takes at most 200 per call: it mails one at a time,
      // paced for Resend's rate limit, inside a 9-minute function. A larger
      // audience goes out as consecutive calls of 200 against the same draft,
      // which already accumulates sentCount across calls.
      const fn = httpsCallable(getFunctions(), 'sendAnnouncement', { timeout: 540000 });
      const all = annChosen.map(({ email, firstName, lastName }) => ({ email, firstName, lastName }));
      const BATCH = 200;
      const batches = [];
      for (let i = 0; i < all.length; i += BATCH) batches.push(all.slice(i, i + BATCH));
      let sent = 0, skipped = 0, failed = 0;
      const failedList = [];
      let stoppedAt = -1, stopError = '';
      for (let b = 0; b < batches.length; b++) {
        setAnnProgress({ batch: b + 1, of: batches.length, sent });
        try {
          const r = await fn({ audience: annAudience, draftId, recipients: batches[b] });
          sent += r.data.sent || 0;
          skipped += r.data.skipped || 0;
          failed += r.data.failed || 0;
          failedList.push(...(r.data.results || []).filter(x => !x.ok && !x.skipped).map(f => f.email));
        } catch (e) {
          // Stop rather than press on: a batch that timed out may have mailed
          // part of its list, and every sent address is already logged. The
          // reloaded list marks who got it, so a resend can skip them.
          stoppedAt = b; stopError = e.message;
          logError('Documents.sendAnnouncement', e.message, `batch ${b + 1} of ${batches.length}`);
          break;
        }
      }
      const unsent = stoppedAt >= 0 ? batches.slice(stoppedAt).reduce((n, x) => n + x.length, 0) : 0;
      setAnnResult({
        severity: stoppedAt >= 0 ? 'error' : failed === 0 ? 'success' : 'warning',
        text: (batches.length > 1 ? `${batches.length} batches: ` : '')
          + `${sent} sent`
          + (skipped ? `, ${skipped} skipped (unsubscribed since the list loaded)` : '')
          + `, ${failed} failed.`
          + (failedList.length ? ' Failed: ' + failedList.join(', ') + '.' : '')
          + (stoppedAt >= 0
            ? ` Stopped at batch ${stoppedAt + 1} of ${batches.length} (${stopError}) — up to ${unsent} not sent.`
              + ' The list below now shows who already received it; select the rest and send again.'
            : ''),
      });
      setAnnSelected({});
      // Refresh both: the recipient list picks up new lastSentAt badges, and
      // the draft list picks up the sent stamp the function just wrote.
      try { setAnnRecipients(await loadAnnouncementAudience()); } catch (e) { /* keep list */ }
      try { await loadDrafts(); } catch (e) { /* keep list */ }
    } catch (e) {
      logError('Documents.sendAnnouncement', e.message, e.stack);
      setAnnResult({ severity: 'error', text: 'Send failed: ' + e.message });
    } finally { setAnnSending(false); setAnnProgress(null); }
  };

  // ── Email template handlers ────────────────────────────────────────────────
  const openTemplateEdit = (tpl) => {
    setEditingTemplateId(tpl.id);
    setTemplateForm({
      name: tpl.name || '',
      description: tpl.description || '',
      subject: tpl.subject || '',
      fields: tpl.fields || {},
      // Carried through so a template that states the order its fields should be
      // edited in gets it. Without this the form fell back to whatever order the
      // keys happened to come back in, which put step 3 above the heading.
      fieldOrder: Array.isArray(tpl.fieldOrder) ? tpl.fieldOrder : null,
      placeholders: tpl.placeholders || [],
      enabled: tpl.enabled !== false,
    });
    setTemplateDialogOpen(true);
  };

  const handlePreview = async () => {
    if (!editingTemplateId) return;
    setPreviewLoading(true);
    try {
      const functions = getFunctions();
      const fn = httpsCallable(functions, 'previewEmailTemplate');
      const result = await fn({
        templateId: editingTemplateId,
        subject: templateForm.subject,
        fields: templateForm.fields,
        images: templateForm.images,
      });
      setPreviewData(result.data);
      setPreviewOpen(true);
    } catch (e) {
      logError('Documents.handlePreview', e.message, e.stack);
      alert('Preview failed: ' + e.message);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleSendTest = async () => {
    if (!editingTemplateId) return;
    const to = window.prompt('Send test email to:', 'steve@torchvolleyball.com');
    if (!to) return;
    setSendingTest(true);
    try {
      const functions = getFunctions();
      const fn = httpsCallable(functions, 'sendTestEmail');
      const result = await fn({
        templateId: editingTemplateId,
        to,
        subject: templateForm.subject,
        fields: templateForm.fields,
        images: templateForm.images,
      });
      alert(`Sent to ${result.data.to}`);
    } catch (e) {
      logError('Documents.handleSendTest', e.message, e.stack);
      alert('Send test failed: ' + e.message);
    } finally {
      setSendingTest(false);
    }
  };

  const handleSaveTemplate = async () => {
    if (!editingTemplateId) return;
    setSaving(true);
    setError('');
    try {
      const data = {
        name: templateForm.name,
        description: templateForm.description,
        subject: templateForm.subject,
        fields: templateForm.fields,
        enabled: templateForm.enabled,
        updatedAt: serverTimestamp(),
      };
      await updateDoc(doc(db, 'emailTemplates', editingTemplateId), data);
      setTemplates(prev => prev.map(t =>
        t.id === editingTemplateId ? { ...t, ...data } : t
      ));
      setTemplateDialogOpen(false);
      setSuccess('Template saved.');
      setTimeout(() => setSuccess(''), 3000);
    } catch (e) {
      setError('Failed to save template.');
      logError('Documents', e.message, e.stack);
    } finally {
      setSaving(false);
    }
  };

  const openNew = () => {
    setEditingId(null);
    setForm({ ...emptyDoc, sortOrder: docs.length });
    setSlugTouched(false); // brand-new doc: auto-sync slug from name
    setDialogOpen(true);
  };

  const openEdit = (d) => {
    setEditingId(d.id);
    const slug = d.slug || '';
    setForm({
      name: d.name || '',
      slug,
      description: d.description || '',
      content: d.content || '',
      audience: Array.isArray(d.audience) ? d.audience : [],
      actionType: d.actionType || 'accept',
      expirationDays: d.expirationDays == null ? '' : String(d.expirationDays),
      enabled: d.enabled !== false,
      sortOrder: d.sortOrder ?? 0,
    });
    // When editing an existing doc, treat the slug as user-managed so we don't
    // overwrite it the moment the admin tweaks the name.
    setSlugTouched(true);
    setDialogOpen(true);
  };

  const toggleAudience = (val) => {
    setForm(f => {
      const cur = f.audience || [];
      const next = cur.includes(val) ? cur.filter(v => v !== val) : [...cur, val];
      return { ...f, audience: next };
    });
  };

  const save = async () => {
    if (!form.name.trim()) { setError('Document name is required.'); return; }
    // Slug validation: required, only lowercase letters/numbers/dashes, unique across collection.
    const slug = toSlug(form.slug || form.name);
    if (!slug) { setError('Slug is required (use letters, numbers, and dashes).'); return; }
    const slugConflict = docs.find(d => d.slug === slug && d.id !== editingId);
    if (slugConflict) {
      setError(`Slug "${slug}" is already used by "${slugConflict.name}". Choose a different slug.`);
      return;
    }
    setSaving(true); setError('');
    try {
      const expDays = String(form.expirationDays ?? '').trim();
      const data = {
        name: form.name.trim(),
        slug,
        description: form.description.trim(),
        content: form.content || '',
        audience: form.audience,
        actionType: form.actionType,
        expirationDays: expDays === '' ? 0 : (parseInt(expDays, 10) || 0),
        enabled: !!form.enabled,
        sortOrder: parseInt(form.sortOrder, 10) || 0,
        updatedAt: serverTimestamp(),
      };
      if (editingId) {
        await updateDoc(doc(db, 'clubDocuments', editingId), data);
        setDocs(prev => prev.map(d => d.id === editingId ? { id: editingId, ...d, ...data } : d)
          .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0)));
      } else {
        const ref = await addDoc(collection(db, 'clubDocuments'), { ...data, createdAt: serverTimestamp() });
        setDocs(prev => [...prev, { id: ref.id, ...data }]
          .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0)));
      }
      setDialogOpen(false);
      setSuccess('Document saved.');
      setTimeout(() => setSuccess(''), 3000);
    } catch (e) {
      logError('Documents', e.message, e.stack);
      setError('Failed to save document.');
    }
    setSaving(false);
  };

  const remove = async (d) => {
    if (!window.confirm(`Delete "${d.name}"? Any existing user acceptances will remain but won't be linked to anything.`)) return;
    try {
      await deleteDoc(doc(db, 'clubDocuments', d.id));
      setDocs(prev => prev.filter(x => x.id !== d.id));
      setSuccess('Document deleted.');
      setTimeout(() => setSuccess(''), 3000);
    } catch (e) {
      logError('Documents', e.message, e.stack);
      setError('Failed to delete.');
    }
  };

  // Apply a preset width to the currently-selected image in the editor.
  // Triggers a Quill text-change so our state mirror picks up the new HTML.
  const resizeSelectedImage = (widthCss) => {
    const img = selectedImageRef.current;
    const q = quillInstanceRef.current;
    if (!img || !q) return;
    img.setAttribute('width', widthCss);
    img.style.width = widthCss;
    // Quill doesn't fire text-change on direct DOM mutation; force a snapshot.
    setForm(f => ({ ...f, content: q.root.innerHTML }));
  };



  // ── Render ─────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <Layout>
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
          <CircularProgress />
        </Box>
      </Layout>
    );
  }

  return (
    <Layout>
      <Box sx={{ p: 3 }}>
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 'bold', color: branding.chromeBg }}>
              Content
            </Typography>
            <Typography variant="body2" sx={{ color: '#888', mt: 0.5 }}>
              {tab === 0
                ? 'Waivers, terms, opt-ins, and other shareable documents.'
                : 'Transactional email templates rendered by Cloud Functions.'}
            </Typography>
          </Box>
          {tab === 0 && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={openNew} sx={{ backgroundColor: branding.chromeBg }}>
              New Document
            </Button>
          )}
        </Box>

        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2, borderBottom: '1px solid #eee' }}>
          <Tab label="Documents" />
          <Tab label="Email Templates" />
          <Tab label="Group Rental Invites" />
        </Tabs>

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

        {tab === 2 && <GroupRentalInvitePanel />}

        {tab === 0 && (docs.length === 0 ? (
          <Paper elevation={1} sx={{ p: 4, textAlign: 'center', borderRadius: 2, border: '1px dashed #ccc' }}>
            <DescriptionIcon sx={{ fontSize: 48, color: '#ccc', mb: 1 }} />
            <Typography sx={{ color: '#888' }}>
              No documents yet. Create your first waiver or terms doc above.
            </Typography>
          </Paper>
        ) : (
          <Grid container spacing={2}>
            {docs.map(d => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={d.id}>
                <Paper elevation={1} sx={{ p: 2.5, borderRadius: 2, border: '1px solid #eee', opacity: d.enabled === false ? 0.55 : 1, height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: branding.chromeBg, wordBreak: 'break-word' }}>
                        {d.name}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 0.5, mt: 0.3, flexWrap: 'wrap' }}>
                        {d.enabled === false && <Chip label="Disabled" size="small" sx={{ fontSize: '0.65rem', height: 18 }} />}
                        {(d.audience || []).includes('everyone') ? (
                          <Tooltip title="Publicly readable — no login required">
                            <Chip icon={<VisibilityIcon style={{ fontSize: 12 }} />} label="Public"
                              size="small" sx={{ fontSize: '0.65rem', height: 18 }} />
                          </Tooltip>
                        ) : (
                          <Tooltip title="Login required — limited to selected audiences">
                            <Chip icon={<VisibilityOffIcon style={{ fontSize: 12 }} />} label="Restricted"
                              size="small" sx={{ fontSize: '0.65rem', height: 18 }} />
                          </Tooltip>
                        )}
                      </Box>
                    </Box>
                    <Box sx={{ flexShrink: 0 }}>
                      <Tooltip title="Preview">
                        <IconButton size="small" onClick={() => setPreviewDoc(d)}>
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <IconButton size="small" onClick={() => openEdit(d)}><EditIcon fontSize="small" /></IconButton>
                      <IconButton size="small" color="error" onClick={() => remove(d)}><DeleteIcon fontSize="small" /></IconButton>
                    </Box>
                  </Box>
                  {d.description && (
                    <Typography variant="caption" sx={{ color: '#666', mb: 1, display: 'block' }}>
                      {d.description}
                    </Typography>
                  )}
                  <Divider sx={{ my: 1 }} />
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, flexGrow: 1 }}>
                    <Typography variant="caption" sx={{ color: '#888' }}>
                      <strong>Action:</strong> {ACTION_TYPE_LABELS[d.actionType] || d.actionType || '—'}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#888' }}>
                      <strong>Audience:</strong> {(d.audience || []).map(a => AUDIENCES.find(x => x.value === a)?.label || a).join(', ') || '—'}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#888' }}>
                      <strong>Expires:</strong> {d.expirationDays > 0 ? `${d.expirationDays} days after acceptance` : 'Never'}
                    </Typography>
                    {d.slug && (
                      <Tooltip title={`Click to copy: ${window.location.origin}/docs/${d.slug}`}>
                        <Typography
                          variant="caption"
                          onClick={(e) => {
                            // Prevent the click from bubbling to any parent
                            // handler (e.g. card click) before copying.
                            e.stopPropagation();
                            const url = `${window.location.origin}/docs/${d.slug}`;
                            navigator.clipboard.writeText(url);
                            setSuccess('Link copied to clipboard.');
                            setTimeout(() => setSuccess(''), 2000);
                          }}
                          sx={{
                            color: '#aaa', fontFamily: 'monospace', fontSize: '0.65rem',
                            mt: 0.5, cursor: 'pointer', display: 'inline-block',
                            '&:hover': { color: branding.chromeBg, textDecoration: 'underline' },
                          }}
                        >
                          /docs/{d.slug}
                        </Typography>
                      </Tooltip>
                    )}
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        ))}

        {/* ─── Tab 1: Email Templates ─────────────────────────────────────── */}
        {tab === 1 && (templates.length === 0 ? (
          <Paper elevation={1} sx={{ p: 4, textAlign: 'center', borderRadius: 2, border: '1px dashed #ccc' }}>
            <DescriptionIcon sx={{ fontSize: 48, color: '#ccc', mb: 1 }} />
            <Typography sx={{ color: '#888' }}>
              No email templates yet. Run the seed script to create initial templates.
            </Typography>
          </Paper>
        ) : (
          <Grid container spacing={2}>
            {!templates.some(t => t.id === 'welcome-email-junior') && (
              <Grid size={{ xs: 12 }}>
                <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: '1px dashed #bbb', display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                  <Box sx={{ flex: 1, minWidth: 260 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: branding.chromeBg }}>
                      Split the welcome email into Junior and Adult
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#888' }}>
                      Creates two separately editable templates with a rich-text body, starting from
                      your current wording. The existing welcome email is left untouched, and is still
                      used until the new ones exist.
                    </Typography>
                  </Box>
                  <Button variant="outlined" disabled={seedingWelcome} onClick={seedWelcome}>
                    {seedingWelcome ? 'Creating…' : 'Create templates'}
                  </Button>
                </Paper>
              </Grid>
            )}
            {!templates.some(t => t.id === 'member-announcement') && (
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <Paper elevation={1} sx={{ p: 2.5, borderRadius: 2, border: '1px dashed #bbb', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', textAlign: 'center' }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: branding.chromeBg, mb: 0.5 }}>
                    Member Announcement
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#666', fontSize: '0.8rem', mb: 1.5 }}>
                    Write club news and send it to a chosen audience, with an unsubscribe link. First open creates the template.
                  </Typography>
                  <Button variant="contained" onClick={openAnnouncement} sx={{ backgroundColor: branding.chromeBg }}>
                    Compose &amp; Send
                  </Button>
                </Paper>
              </Grid>
            )}
            {!templates.some(t => t.id === 'member-migration') && (
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <Paper elevation={1} sx={{ p: 2.5, borderRadius: 2, border: '1px dashed #bbb', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', textAlign: 'center' }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: branding.chromeBg, mb: 0.5 }}>
                    Member Migration Campaign
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#666', fontSize: '0.8rem', mb: 1.5 }}>
                    Email unlinked vb-scores members to create their Torch account. First open creates the editable template.
                  </Typography>
                  <Button variant="contained" onClick={openCampaign} sx={{ backgroundColor: branding.chromeBg }}>
                    Set Up &amp; Send Campaign
                  </Button>
                </Paper>
              </Grid>
            )}
            {templates.map(t => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={t.id}>
                <Paper elevation={1} sx={{ p: 2.5, borderRadius: 2, border: '1px solid #eee', opacity: t.enabled === false ? 0.55 : 1, height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: branding.chromeBg, wordBreak: 'break-word' }}>
                        {t.name}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 0.5, mt: 0.3, flexWrap: 'wrap' }}>
                        <Chip label={t.id} size="small" sx={{ fontSize: '0.65rem', height: 18, fontFamily: 'monospace' }} />
                        {t.enabled === false && <Chip label="Disabled" size="small" sx={{ fontSize: '0.65rem', height: 18 }} />}
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      {t.id === 'member-migration' && (
                        <Tooltip title="Send this template to unlinked members">
                          <Button size="small" variant="contained" onClick={openCampaign}
                            sx={{ backgroundColor: branding.chromeBg, fontSize: '0.7rem', minWidth: 0, px: 1.2 }}>
                            Send Campaign
                          </Button>
                        </Tooltip>
                      )}
                      {t.id === 'member-announcement' && (
                        <Tooltip title="Write the announcement and send it, in one place">
                          <Button size="small" variant="contained" onClick={openAnnouncement}
                            sx={{ backgroundColor: branding.chromeBg, fontSize: '0.7rem', minWidth: 0, px: 1.2 }}>
                            Compose &amp; Send
                          </Button>
                        </Tooltip>
                      )}
                      {/* Announcements are composed in the send dialog now. The
                          pencil stays for the admin-only fields it alone covers
                          — name, description, enabled — and because every other
                          template still needs it. */}
                      <Tooltip title="Edit template">
                        <IconButton size="small" onClick={() => openTemplateEdit(t)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                  {t.description && (
                    <Typography variant="body2" sx={{ color: '#666', fontSize: '0.8rem', mb: 1 }}>
                      {t.description}
                    </Typography>
                  )}
                  <Typography variant="caption" sx={{ color: '#888', display: 'block', mt: 'auto' }}>
                    <strong>Subject:</strong> {t.subject || '—'}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#888', display: 'block', mt: 0.5 }}>
                    <strong>Placeholders:</strong> {(t.placeholders || []).map(p => p.key).join(', ') || '—'}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        ))}

        {/* Edit / Create Dialog */}
        <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle sx={{ backgroundColor: branding.chromeBg, color: 'white' }}>
            {editingId ? 'Edit Document' : 'New Document'}
          </DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  fullWidth label="Document Name *" value={form.name}
                  onChange={e => {
                    const name = e.target.value;
                    setForm(f => ({
                      ...f,
                      name,
                      // Auto-sync slug from name only while admin hasn't edited it manually.
                      slug: slugTouched ? f.slug : toSlug(name),
                    }));
                  }}
                  placeholder="e.g. Junior Waiver"
                  sx={{ backgroundColor: '#eeeeee', borderRadius: 1 }}
                />
                <TextField
                  label="Sort" type="number" value={form.sortOrder}
                  onChange={e => setForm(f => ({ ...f, sortOrder: e.target.value }))}
                  inputProps={{ min: 0 }}
                  sx={{ width: 90, backgroundColor: '#eeeeee', borderRadius: 1 }}
                />
              </Box>

              <TextField
                fullWidth label="URL Slug" value={form.slug}
                onChange={e => {
                  // Normalize as the user types; flag as manually-touched so name
                  // changes no longer overwrite it.
                  setSlugTouched(true);
                  setForm(f => ({ ...f, slug: toSlug(e.target.value) }));
                }}
                placeholder="auto-generated from name"
                helperText={form.slug ? `Will be reachable at /docs/${form.slug}` : 'Lowercase letters, numbers, and dashes only'}
                sx={{ backgroundColor: '#eeeeee', borderRadius: 1 }}
                InputProps={{
                  startAdornment: (
                    <Typography variant="caption" sx={{ color: '#888', mr: 0.5 }}>/docs/</Typography>
                  ),
                }}
              />

              <TextField
                fullWidth label="Description (admin-only note)" value={form.description}
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                placeholder="What this document is for; not shown to users"
                multiline rows={2}
                sx={{ backgroundColor: '#eeeeee', borderRadius: 1 }}
              />

              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#555', mb: 1 }}>
                  Audience *
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {AUDIENCES.map(a => {
                    const sel = (form.audience || []).includes(a.value);
                    return (
                      <Box
                        key={a.value}
                        onClick={() => toggleAudience(a.value)}
                        sx={{
                          px: 1.5, py: 0.6, borderRadius: 1, cursor: 'pointer',
                          fontSize: '0.82rem', fontWeight: 600,
                          border: `2px solid ${sel ? branding.chromeBg : '#ddd'}`,
                          backgroundColor: sel ? branding.chromeBg : 'white',
                          color: sel ? 'white' : '#555',
                          '&:hover': { borderColor: branding.chromeBg },
                        }}
                      >
                        {a.label}
                      </Box>
                    );
                  })}
                </Box>
              </Box>

              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <TextField
                  select fullWidth label="Required Action" value={form.actionType}
                  onChange={e => setForm(f => ({ ...f, actionType: e.target.value }))}
                  sx={{ flex: '1 1 300px', backgroundColor: '#eeeeee', borderRadius: 1 }}
                >
                  {ACTION_TYPES.map(a => <MenuItem key={a.value} value={a.value}>{a.label}</MenuItem>)}
                </TextField>
                <TextField
                  fullWidth label="Expiration (days)" type="number"
                  value={form.expirationDays}
                  onChange={e => setForm(f => ({ ...f, expirationDays: e.target.value }))}
                  helperText="Days after acceptance until it expires. Blank or 0 = never expires."
                  inputProps={{ min: 0, step: 1 }}
                  sx={{ flex: '1 1 180px', backgroundColor: '#eeeeee', borderRadius: 1 }}
                />
              </Box>

              <Divider><Typography variant="caption" sx={{ color: '#888' }}>Document Body</Typography></Divider>

              {/* Image size presets. Click an image inside the editor to
                  enable these buttons; the chosen width is applied via
                  inline width attribute + style. */}
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                <Typography variant="caption" sx={{ color: '#888' }}>
                  {hasSelectedImage ? 'Resize selected image:' : 'Click an image to enable resize:'}
                </Typography>
                <Button size="small" variant="outlined" disabled={!hasSelectedImage}
                  onClick={() => resizeSelectedImage('150px')}>Small</Button>
                <Button size="small" variant="outlined" disabled={!hasSelectedImage}
                  onClick={() => resizeSelectedImage('300px')}>Medium</Button>
                <Button size="small" variant="outlined" disabled={!hasSelectedImage}
                  onClick={() => resizeSelectedImage('500px')}>Large</Button>
                <Button size="small" variant="outlined" disabled={!hasSelectedImage}
                  onClick={() => resizeSelectedImage('100%')}>Full</Button>
              </Box>

              {/* Quill mounts here on dialog open. */}
              <Box sx={{ backgroundColor: 'white', borderRadius: 1 }}>
                <div ref={quillContainerRef} style={{ minHeight: 280 }} />
              </Box>

              <FormControlLabel
                control={<Switch checked={!!form.enabled} onChange={e => setForm(f => ({ ...f, enabled: e.target.checked }))} />}
                label={form.enabled ? 'Enabled — available for use' : 'Disabled — hidden from all flows'}
              />
            </Box>
              {/* Validation: scan fields/subject for [[placeholders]] and compare
                  against the declared placeholders list. Shows warnings for typos
                  and unused declared keys. */}
              {(() => {
                const declared = new Set((templateForm.placeholders || []).map(p => p.key));
                const used = new Set();
                const RE = /\[\[([a-zA-Z0-9_]+)\]\]/g;
                const texts = [templateForm.subject, ...Object.values(templateForm.fields || {})];
                for (const t of texts) {
                  if (typeof t !== 'string') continue;
                  let m;
                  while ((m = RE.exec(t)) !== null) used.add(m[1]);
                }
                const orphans = [...used].filter(k => !declared.has(k));
                const unused = [...declared].filter(k => !used.has(k));
                if (orphans.length === 0 && unused.length === 0) return null;
                return (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {orphans.length > 0 && (
                      <Alert severity="warning" sx={{ py: 0.5 }}>
                        Referenced but not declared: {orphans.map(k => `[[${k}]]`).join(', ')}.
                        Add them to Placeholders below or they will render blank.
                      </Alert>
                    )}
                    {unused.length > 0 && (
                      <Alert severity="info" sx={{ py: 0.5 }}>
                        Declared but unused: {unused.map(k => `[[${k}]]`).join(', ')}.
                      </Alert>
                    )}
                  </Box>
                );
              })()}
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button variant="contained" onClick={save} disabled={saving} sx={{ backgroundColor: branding.chromeBg }}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Preview Dialog (read-only) */}
        <Dialog open={!!previewDoc} onClose={() => setPreviewDoc(null)} maxWidth="md" fullWidth>
          <DialogTitle sx={{ backgroundColor: branding.chromeBg, color: 'white' }}>
            {previewDoc?.name}
          </DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            {previewDoc && (
              <Box>
                <Typography variant="caption" sx={{ color: '#888', display: 'block', mb: 2 }}>
                  Audience: {(previewDoc.audience || []).map(a => AUDIENCES.find(x => x.value === a)?.label || a).join(', ') || '—'}
                  {' · '}Action: {ACTION_TYPE_LABELS[previewDoc.actionType] || previewDoc.actionType}
                  {previewDoc.expirationDays > 0 ? ` · Expires after ${previewDoc.expirationDays} days` : ' · Never expires'}
                </Typography>
                {/* Rendering the saved Quill HTML. Content is admin-authored
                    and trusted, but if ever opened to non-admins we should
                    sanitize through DOMPurify. */}
                <Box
                  sx={{ '& p': { margin: '0.5em 0' }, '& h1,h2,h3': { color: branding.chromeBg }, fontSize: '0.95rem' }}
                  dangerouslySetInnerHTML={{ __html: previewDoc.content || '<em>No content.</em>' }}
                />
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setPreviewDoc(null)}>Close</Button>
          </DialogActions>
        </Dialog>

        {/* ─── Email Template Edit Dialog ───────────────────────────────── */}
        <Dialog open={templateDialogOpen} onClose={() => setTemplateDialogOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle sx={{ backgroundColor: branding.chromeBg, color: 'white' }}>
            Edit Email Template — {editingTemplateId}
          </DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <TextField
                fullWidth
                label="Display Name"
                value={templateForm.name}
                onChange={e => setTemplateForm(f => ({ ...f, name: e.target.value }))}
              />
              <TextField
                fullWidth
                label="Description (admin-only note)"
                value={templateForm.description}
                onChange={e => setTemplateForm(f => ({ ...f, description: e.target.value }))}
                multiline
                rows={2}
              />
              <TextField
                fullWidth
                label="Subject"
                value={templateForm.subject}
                onChange={e => setTemplateForm(f => ({ ...f, subject: e.target.value }))}
                helperText="Use [[placeholderName]] for variable substitution"
              />
              {Object.keys(templateForm.fields || {}).length > 0 && (
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                    Email Content
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#888', display: 'block', mb: 1.5 }}>
                    Edit the text shown in the email. Use [[placeholderName]] for variables (see reference below).
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {(templateForm.fieldOrder || Object.keys(templateForm.fields || {})).map(key => {
                      const value = templateForm.fields[key];
                      if (value === undefined) return null;
                      const label = key.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase());
                      const isMultiline = typeof value === 'string' && (value.length > 80 || value.includes('\n'));
                      // The `body` field is authored as rich HTML — a plain
                      // textarea would mean hand-writing email markup.
                      if (key === 'body') {
                        return (
                          <Box key={key}>
                            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>{label}</Typography>
                            <EmailBodyEditor
                              value={value}
                              variables={(templateForm.placeholders || []).map(p => p.key)}
                              onChange={html => setTemplateForm(f => ({
                                ...f,
                                fields: { ...f.fields, body: html },
                              }))}
                            />
                          </Box>
                        );
                      }
                      return (
                        <TextField
                          key={key}
                          fullWidth
                          label={label}
                          value={value}
                          multiline={isMultiline}
                          rows={isMultiline ? 3 : 1}
                          onChange={e => setTemplateForm(f => ({
                            ...f,
                            fields: { ...f.fields, [key]: e.target.value },
                          }))}
                        />
                      );
                    })}
                  </Box>
                </Box>
              )}
              {(templateForm.placeholders || []).length > 0 && (
                <Paper variant="outlined" sx={{ p: 2, backgroundColor: '#fafafa' }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: branding.chromeBg }}>
                    Available Placeholders
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    {templateForm.placeholders.map((p) => (
                      <Box key={p.key} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                        <Chip
                          label={`[[${p.key}]]`}
                          size="small"
                          sx={{ fontFamily: 'monospace', fontSize: '0.7rem', minWidth: 'auto' }}
                        />
                        <Typography variant="caption" sx={{ color: '#666', flex: 1 }}>
                          {p.description}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                  <Typography variant="caption" sx={{ color: '#888', display: 'block', mt: 1.5, fontStyle: 'italic' }}>
                    To add a new placeholder, the Cloud Function code must be updated too.
                  </Typography>
                </Paper>
              )}
              <FormControlLabel
                control={
                  <Switch
                    checked={templateForm.enabled}
                    onChange={e => setTemplateForm(f => ({ ...f, enabled: e.target.checked }))}
                  />
                }
                label="Enabled"
              />
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setTemplateDialogOpen(false)} disabled={saving}>
              Cancel
            </Button>
            <Button
              onClick={handlePreview}
              disabled={previewLoading || !editingTemplateId}
              startIcon={previewLoading ? <CircularProgress size={14} /> : null}
            >
              Preview
            </Button>
            <Button
              onClick={handleSendTest}
              disabled={sendingTest || !editingTemplateId}
              startIcon={sendingTest ? <CircularProgress size={14} /> : null}
            >
              Send Test
            </Button>
            <Button
              variant="contained"
              onClick={handleSaveTemplate}
              disabled={saving || !templateForm.subject || Object.keys(templateForm.fields || {}).length === 0}
              sx={{ backgroundColor: branding.chromeBg }}
            >
              {saving ? 'Saving...' : 'Save Template'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Email preview dialog */}
        <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle>Email Preview</DialogTitle>
          <DialogContent dividers>
            <Box sx={{ mb: 2, p: 1.5, backgroundColor: '#f8f9fa', borderRadius: 1, border: '1px solid #eee' }}>
              <Typography variant="caption" sx={{ color: '#888', display: 'block' }}>SUBJECT</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{previewData.subject}</Typography>
            </Box>
            <Box sx={{ border: '1px solid #ddd', borderRadius: 1, overflow: 'hidden', backgroundColor: '#fff' }}>
              <iframe
                srcDoc={previewData.html}
                sandbox=""
                style={{ width: '100%', height: '600px', border: 'none', display: 'block' }}
                title="Email Preview"
              />
            </Box>
            <Typography variant="caption" sx={{ color: '#888', display: 'block', mt: 1.5 }}>
              Rendered with sample placeholder values. Real sends will substitute live data.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPreviewOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      </Box>
      {/* ── Member announcement dialog ── */}
      <Dialog open={annOpen} onClose={() => !annSending && setAnnOpen(false)} fullWidth maxWidth="lg">
        <DialogTitle>Compose &amp; Send Announcement</DialogTitle>
        <DialogContent dividers>
          {annResult && <Alert severity={annResult.severity} onClose={() => setAnnResult(null)} sx={{ mb: 2 }}>{annResult.text}</Alert>}
          {annLoading ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <CircularProgress size={28} />
              <Typography variant="caption" sx={{ display: 'block', mt: 1, color: '#888' }}>Loading…</Typography>
            </Box>
          ) : (
            <Grid container spacing={3}>
            {/* ── Left: the message ── */}
            <Grid size={{ xs: 12, md: 7 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: branding.chromeBg }}>
                  Message
                </Typography>
                {annDirty && <Chip label="unsaved" size="small" color="warning" sx={{ fontSize: '0.62rem', height: 18 }} />}
                {annCurrentDraft?.lastSentAt && (
                  <Chip size="small" color="info" variant="outlined" sx={{ fontSize: '0.62rem', height: 18 }}
                    label={`sent ${new Date(annCurrentDraft.lastSentAt.toMillis()).toLocaleDateString()}`
                      + (annCurrentDraft.sentCount ? ` · ${annCurrentDraft.sentCount}` : '')} />
                )}
              </Box>
              <Box sx={{ display: 'flex', gap: 1, mb: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
                <TextField
                  select size="small" label="Draft" value={annDraftId} sx={{ flexGrow: 1, minWidth: 200 }}
                  onChange={e => annPickDraft(e.target.value)}
                  // A new draft's id is '', which MUI renders as an empty box
                  // unless told to display it — and a blank dropdown reads as
                  // broken rather than as "nothing saved yet".
                  SelectProps={{ displayEmpty: true }}
                  InputLabelProps={{ shrink: true }}
                >
                  {annDraftId === '' && <MenuItem value="">New draft (unsaved)</MenuItem>}
                  {annDrafts.map(d => (
                    <MenuItem key={d.id} value={d.id}>
                      {d.lastSentAt ? '✓ ' : ''}{d.subject || '(no subject)'}
                      {d.updatedAt ? ` — ${new Date(d.updatedAt.toMillis()).toLocaleDateString()}` : ''}
                    </MenuItem>
                  ))}
                  {annDrafts.length === 0 && annDraftId !== '' && <MenuItem value={annDraftId}>(loading…)</MenuItem>}
                </TextField>
                <Button size="small" onClick={annNewDraft}>New</Button>
                <Button size="small" variant="outlined" onClick={annSaveDraft} disabled={annSavingDraft || !annDirty}
                  startIcon={annSavingDraft ? <CircularProgress size={13} /> : null}>
                  Save
                </Button>
                <Button size="small" color="error" onClick={annDeleteDraft} disabled={!annDraftId}>Delete</Button>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <TextField
                  fullWidth size="small" label="Subject" value={annForm.subject || ''}
                  onChange={e => editAnn(f => ({ ...f, subject: e.target.value }))}
                  helperText="Also names the draft. [[firstName]] merges here too."
                />
                <TextField
                  fullWidth size="small" label="Headline" value={annForm.fields?.headline || ''}
                  onChange={e => editAnn(f => ({ ...f, fields: { ...f.fields, headline: e.target.value } }))}
                />
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>Body</Typography>
                  <EmailBodyEditor
                    value={annForm.fields?.body || ''}
                    variables={['firstName', 'lastName', 'email', 'appUrl', 'clubName']}
                    onChange={html => editAnn(f => ({ ...f, fields: { ...f.fields, body: html } }))}
                  />
                </Box>
                <TextField
                  fullWidth size="small" label="Footer Text" value={annForm.fields?.footerText || ''}
                  onChange={e => editAnn(f => ({ ...f, fields: { ...f.fields, footerText: e.target.value } }))}
                  helperText="Keep the mailing address here — commercial email is required to carry one."
                />
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', flexWrap: 'wrap', mt: 0.5 }}>
                  <Button size="small" variant="outlined" onClick={annPreview} disabled={previewLoading}
                    startIcon={previewLoading ? <CircularProgress size={13} /> : null}>
                    Preview
                  </Button>
                  <TextField
                    size="small" label="Test to" value={annTestTo} sx={{ flexGrow: 1, minWidth: 190 }}
                    onChange={e => setAnnTestTo(e.target.value)}
                  />
                  <Button size="small" variant="outlined" onClick={annSendTest} disabled={annTesting}
                    startIcon={annTesting ? <CircularProgress size={13} /> : null}>
                    Send test
                  </Button>
                </Box>
                <Typography variant="caption" sx={{ color: '#888' }}>
                  Preview and test use what's on screen, saved or not. A test send signs the unsubscribe
                  link for the address you send it to, so clicking it really does unsubscribe that address.
                </Typography>
              </Box>
            </Grid>

            {/* ── Right: who gets it ── */}
            <Grid size={{ xs: 12, md: 5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: branding.chromeBg, mb: 1.5 }}>
                Recipients
              </Typography>
              <TextField
                select fullWidth size="small" label="Audience" value={annAudience}
                onChange={e => changeAudience(e.target.value)}
                sx={{ mb: 1.5 }}
              >
                {annAudiences.map(a => (
                  <MenuItem key={a.id} value={a.id}>
                    {a.label} — {a.mailable}
                    {a.total !== a.mailable && ` of ${a.total}`}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                fullWidth size="small" label="Search name or email" value={annSearch}
                onChange={e => setAnnSearch(e.target.value)}
                sx={{ mb: 1.5 }}
              />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="body2" sx={{ color: '#666' }}>
                  {/* "shown" only appears while searching: with an empty box it
                      would just repeat the mailable count back. */}
                  {annQuery && `${annShown.length} shown · `}
                  {annSendable.length} mailable · {annChosen.length} selected
                  {annVisible.length - annSendable.length > 0 && ` · ${annVisible.length - annSendable.length} unsubscribed`}
                </Typography>
                <Box>
                  <Button size="small" onClick={() => {
                    // "All" means every mailable row you can currently SEE,
                    // added to what is already ticked — so searching a family
                    // and hitting All builds the selection up rather than
                    // throwing away what the last search picked. Known-bad
                    // addresses are skipped and stay tickable by hand. With an
                    // empty search box this is the whole audience, as before.
                    setAnnSelected(sel => {
                      const next = { ...sel };
                      annShown.forEach(x => { if (!x.optedOut && !x.bounced) next[x.email] = true; });
                      return next;
                    });
                  }}>All</Button>
                  <Button size="small" onClick={() => setAnnSelected({})}>None</Button>
                </Box>
              </Box>
              <Box sx={{ maxHeight: 340, overflowY: 'auto', border: '1px solid #eee', borderRadius: 1, p: 1 }}>
                {annShown.map(rec => (
                  <Box key={rec.email} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.3, borderBottom: '1px solid #f5f5f5', opacity: rec.optedOut ? 0.5 : 1 }}>
                    <Checkbox size="small" disabled={rec.optedOut} checked={!!annSelected[rec.email] && !rec.optedOut}
                      onChange={e => setAnnSelected(s => ({ ...s, [rec.email]: e.target.checked }))} sx={{ p: 0.5 }} />
                    <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                        {`${rec.firstName} ${rec.lastName}`.trim() || '(no name on file)'}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#888', wordBreak: 'break-all' }}>{rec.email}</Typography>
                    </Box>
                    {rec.optedOut && <Chip label={rec.optedOutSource === 'complaint' ? 'spam complaint' : 'unsubscribed'} size="small" color="error" variant="outlined" sx={{ fontSize: '0.62rem', height: 18 }} />}
                    {rec.bounced && <Chip label="bounced" size="small" color="warning" sx={{ fontSize: '0.62rem', height: 18 }} />}
                    {/* Only worth showing where the audience doesn't already say
                        it — under Adults or Parents every row would carry it. */}
                    {annAudience === 'everyone' && rec.isParent && (
                      <Chip label={rec.isAdult ? 'adult + parent' : 'parent'} size="small" variant="outlined" sx={{ fontSize: '0.62rem', height: 18 }} />
                    )}
                    {/* Same idea: pointless under Newsletter subscribers,
                        where every row is one. */}
                    {annAudience !== 'newsletter' && rec.isSubscriber && (
                      <Chip label="newsletter" size="small" variant="outlined" sx={{ fontSize: '0.62rem', height: 18 }} />
                    )}
                    {rec.membershipStatus && rec.membershipStatus !== 'none' && (
                      <Chip label={rec.membershipStatus} size="small" variant="outlined"
                        color={rec.membershipStatus === 'active' || rec.membershipStatus === 'trialing' ? 'success' : 'default'}
                        sx={{ fontSize: '0.62rem', height: 18 }} />
                    )}
                    {rec.lastSentAt && <Chip label={`sent ${new Date(rec.lastSentAt).toLocaleDateString()}`} size="small" color="info" variant="outlined" sx={{ fontSize: '0.62rem', height: 18 }} />}
                  </Box>
                ))}
                {annShown.length === 0 && (
                  <Typography variant="body2" sx={{ color: '#888', p: 2, textAlign: 'center' }}>
                    {annVisible.length === 0
                      ? 'Nobody in this audience.'
                      : `Nobody in this audience matches "${annSearch.trim()}".`}
                  </Typography>
                )}
              </Box>
              <Typography variant="caption" sx={{ color: '#888', display: 'block', mt: 1 }}>
                Membership status picks the audience, never eligibility — "Members" is active or trialing, and a frozen membership counts as paused rather than current, so it sits under Lapsed. Newsletter subscribers signed up on the website; the ones with no account here appear only under Newsletter subscribers and Everyone. Switching audience clears the selection; searching only narrows what you see, so ticks survive it and "All" adds the rows currently shown. Each email carries an unsubscribe link and a Gmail unsubscribe button; unsubscribed addresses can't be selected, and are re-checked at send time in case someone opts out while this list is open. Receipts and booking confirmations are unaffected by an unsubscribe. Over 200 recipients go out automatically in batches of 200, one after another — keep this dialog open until it finishes (about 3 minutes per batch).
              </Typography>
            </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Typography variant="caption" sx={{ color: '#888', mr: 'auto', ml: 1 }}>
            Sending saves the draft first, then mails that saved copy.
          </Typography>
          <Button onClick={() => setAnnOpen(false)} disabled={annSending}>Close</Button>
          <Button variant="contained" onClick={sendAnnouncementNow} sx={{ backgroundColor: branding.chromeBg }}
            disabled={annSending || annLoading || annChosen.length === 0
              || !annForm.subject || !annBodyHasText}>
            {annSending
              ? (annProgress && annProgress.of > 1
                ? `Sending batch ${annProgress.batch} of ${annProgress.of}…`
                : 'Sending…')
              : !annForm.subject ? 'Subject required'
              : !annBodyHasText ? 'Body is empty'
              : annChosen.length > 200
                ? `Send to ${annChosen.length} (${Math.ceil(annChosen.length / 200)} batches)`
                : `Send to ${annChosen.length}`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Member migration campaign dialog ── */}
      <Dialog open={campaignOpen} onClose={() => !campaignSending && setCampaignOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Send Migration Campaign</DialogTitle>
        <DialogContent>
          {campaignResult && <Alert severity={campaignResult.severity} onClose={() => setCampaignResult(null)} sx={{ mb: 2 }}>{campaignResult.text}</Alert>}
          {campaignLoading ? (
            <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress size={28} /><Typography variant="caption" sx={{ display: 'block', mt: 1, color: '#888' }}>Loading unlinked members from Stripe… (up to a minute)</Typography></Box>
          ) : (
            <>
              <TextField
                fullWidth size="small" label="Search name or email" value={campaignSearch}
                onChange={e => setCampaignSearch(e.target.value)}
                sx={{ mb: 1.5 }}
              />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="body2" sx={{ color: '#666' }}>
                  {/* "shown" only while searching — with an empty box it would
                      just repeat the total back. */}
                  {campaignSearch.trim() && `${campaignShown.length} shown · `}
                  {campaignRecipients.length} unlinked member{campaignRecipients.length === 1 ? '' : 's'} · {campaignChosen.length} selected
                </Typography>
                <Box>
                  <Button size="small" onClick={() => {
                    // Every deliverable row currently SHOWN, added to what is
                    // already ticked, so searching a name and hitting All
                    // builds a batch up instead of replacing it. Apple-relay
                    // addresses are skipped rather than force-unticked: they
                    // are undeliverable without an alternate, but a deliberate
                    // hand tick should survive.
                    setCampaignSelected(sel => {
                      const next = { ...sel };
                      campaignShown.forEach(x => { if (!x.appleRelay) next[x.email] = true; });
                      return next;
                    });
                  }}>All</Button>
                  <Button size="small" onClick={() => setCampaignSelected({})}>None</Button>
                </Box>
              </Box>
              <Box sx={{ maxHeight: 340, overflowY: 'auto', border: '1px solid #eee', borderRadius: 1, p: 1 }}>
                {campaignShown.map(rec => (
                  <Box key={rec.email} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.3, borderBottom: '1px solid #f5f5f5' }}>
                    <Checkbox size="small" checked={!!campaignSelected[rec.email]}
                      onChange={e => setCampaignSelected(s => ({ ...s, [rec.email]: e.target.checked }))} sx={{ p: 0.5 }} />
                    <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                        {`${rec.firstName} ${rec.lastName}`.trim() || '(no name in Stripe)'}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#888', wordBreak: 'break-all' }}>{rec.email}</Typography>
                    </Box>
                    {rec.appleRelay && <Chip label="Apple relay" size="small" color="warning" sx={{ fontSize: '0.62rem', height: 18 }} />}
                    {rec.viaRoster && <Chip label="real email" size="small" color="info" variant="outlined" sx={{ fontSize: '0.62rem', height: 18 }} />}
                    {rec.status === 'trialing' && <Chip label="prepaid" size="small" sx={{ fontSize: '0.62rem', height: 18 }} />}
                    {rec.lastSentAt && <Chip label={`sent ${new Date(rec.lastSentAt).toLocaleDateString()}`} size="small" color="success" variant="outlined" sx={{ fontSize: '0.62rem', height: 18 }} />}
                  </Box>
                ))}
                {campaignShown.length === 0 && (
                  <Typography variant="body2" sx={{ color: '#888', p: 2, textAlign: 'center' }}>
                    {campaignRecipients.length === 0
                      ? 'No unlinked members — everyone has registered. Migration complete. 🎉'
                      : `No unlinked member matches "${campaignSearch.trim()}".`}
                  </Typography>
                )}
              </Box>
              <Typography variant="caption" sx={{ color: '#888', display: 'block', mt: 1 }}>
                Each member gets an individually merged email telling them to register with that exact address. “real email” = roster alternate for Apple-relay members (migration matches it automatically). Nothing is pre-selected — pick your batch; searching only narrows what you see, so ticks survive it and "All" adds the rows currently shown. Apple-relay addresses without an alternate are undeliverable — those members need manual linking. Sends are logged; the "sent" badge shows prior campaigns.
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCampaignOpen(false)} disabled={campaignSending}>Close</Button>
          <Button variant="contained" disabled={campaignSending || campaignLoading || campaignChosen.length === 0}
            onClick={sendCampaign} sx={{ backgroundColor: branding.chromeBg }}>
            {campaignSending ? 'Sending…' : `Send to ${campaignChosen.length}`}
          </Button>
        </DialogActions>
      </Dialog>

    </Layout>
  );
}
